// src/components/AIAgent/PlaybookEditor.jsx

import React, { useState, useEffect } from "react";
import { toast } from "react-toastify";
import {
  FaPlus,
  FaTimes,
  FaSave,
  FaBook,
  FaChevronDown,
  FaChevronUp,
  FaQuestionCircle,
  FaExclamationTriangle,
  FaArrowRight,
} from "react-icons/fa";
import { useAIAgentStore } from "../../services/useAIAgentStore";

const PlaybookEditor = () => {
  const {
    playbooksWithStages,
    loading,
    fetchPlaybooksWithStages,
    upsertPlaybook,
    deletePlaybook,
  } = useAIAgentStore();

  const [expandedStage, setExpandedStage] = useState(null);
  const [editingPlaybook, setEditingPlaybook] = useState(null);
  const [formData, setFormData] = useState({
    name: "",
    objective: "",
    opening_message: "",
    questions_to_ask: [],
    objections_handling: [],
    success_criteria: "",
    next_stage_id: "",
    escalation_triggers: [],
    custom_instructions: "",
  });
  const [newQuestion, setNewQuestion] = useState({ question: "", purpose: "" });
  const [newObjection, setNewObjection] = useState({ objection: "", response: "" });
  const [newEscalationTrigger, setNewEscalationTrigger] = useState("");

  useEffect(() => {
    fetchPlaybooksWithStages();
  }, []);

  const toggleStage = (stageId) => {
    if (expandedStage === stageId) {
      setExpandedStage(null);
      setEditingPlaybook(null);
    } else {
      setExpandedStage(stageId);
      const stageData = playbooksWithStages.find((s) => s.stage.id === stageId);
      if (stageData?.playbook) {
        loadPlaybookData(stageData.playbook);
      } else {
        resetForm();
      }
      setEditingPlaybook(stageId);
    }
  };

  const loadPlaybookData = (playbook) => {
    setFormData({
      name: playbook.name || "",
      objective: playbook.objective || "",
      opening_message: playbook.opening_message || "",
      questions_to_ask: playbook.questions_to_ask || [],
      objections_handling: playbook.objections_handling || [],
      success_criteria: playbook.success_criteria || "",
      next_stage_id: playbook.next_stage_id || "",
      escalation_triggers: playbook.escalation_triggers || [],
      custom_instructions: playbook.custom_instructions || "",
    });
  };

  const resetForm = () => {
    setFormData({
      name: "",
      objective: "",
      opening_message: "",
      questions_to_ask: [],
      objections_handling: [],
      success_criteria: "",
      next_stage_id: "",
      escalation_triggers: [],
      custom_instructions: "",
    });
    setNewQuestion({ question: "", purpose: "" });
    setNewObjection({ objection: "", response: "" });
    setNewEscalationTrigger("");
  };

  const handleAddQuestion = () => {
    if (newQuestion.question.trim()) {
      const order = formData.questions_to_ask.length + 1;
      setFormData({
        ...formData,
        questions_to_ask: [...formData.questions_to_ask, { order, ...newQuestion }],
      });
      setNewQuestion({ question: "", purpose: "" });
    }
  };

  const handleRemoveQuestion = (index) => {
    const newQuestions = formData.questions_to_ask
      .filter((_, i) => i !== index)
      .map((q, i) => ({ ...q, order: i + 1 }));
    setFormData({ ...formData, questions_to_ask: newQuestions });
  };

  const handleAddObjection = () => {
    if (newObjection.objection.trim() && newObjection.response.trim()) {
      setFormData({
        ...formData,
        objections_handling: [...formData.objections_handling, { ...newObjection }],
      });
      setNewObjection({ objection: "", response: "" });
    }
  };

  const handleRemoveObjection = (index) => {
    setFormData({
      ...formData,
      objections_handling: formData.objections_handling.filter((_, i) => i !== index),
    });
  };

  const handleAddEscalationTrigger = () => {
    if (
      newEscalationTrigger.trim() &&
      !formData.escalation_triggers.includes(newEscalationTrigger.trim())
    ) {
      setFormData({
        ...formData,
        escalation_triggers: [...formData.escalation_triggers, newEscalationTrigger.trim()],
      });
      setNewEscalationTrigger("");
    }
  };

  const handleRemoveEscalationTrigger = (trigger) => {
    setFormData({
      ...formData,
      escalation_triggers: formData.escalation_triggers.filter((t) => t !== trigger),
    });
  };

  const handleSave = async (stageId) => {
    try {
      await upsertPlaybook(stageId, formData);
      toast.success("Playbook salvo com sucesso!");
    } catch (err) {
      // Error handled by store
    }
  };

  const handleDelete = async (playbookId, stageId) => {
    if (window.confirm("Tem certeza que deseja excluir este playbook?")) {
      try {
        await deletePlaybook(playbookId);
        resetForm();
        toast.success("Playbook excluido com sucesso!");
      } catch (err) {
        // Error handled by store
      }
    }
  };

  const getOtherStages = (currentStageId) => {
    return playbooksWithStages
      .filter((s) => s.stage.id !== currentStageId)
      .map((s) => s.stage);
  };

  return (
    <div className="playbook-section">
      <div className="playbook-header">
        <div>
          <h3>
            <FaBook /> Playbooks por Etapa
          </h3>
          <p>Configure o comportamento do agente em cada etapa do funil</p>
        </div>
      </div>

      {playbooksWithStages.length === 0 ? (
        <div className="empty-playbook">
          <FaBook size={48} />
          <p>Nenhuma etapa encontrada</p>
          <span>Configure as etapas do CRM primeiro</span>
        </div>
      ) : (
        <div className="playbook-list">
          {playbooksWithStages.map((stageData) => (
            <div
              key={stageData.stage.id}
              className={`playbook-card ${expandedStage === stageData.stage.id ? "expanded" : ""}`}
            >
              <div
                className="playbook-card-header"
                onClick={() => toggleStage(stageData.stage.id)}
              >
                <div className="stage-info">
                  <span
                    className="stage-color"
                    style={{ backgroundColor: stageData.stage.color }}
                  />
                  <h4>{stageData.stage.name}</h4>
                  {stageData.has_playbook && (
                    <span className="configured-badge">Configurado</span>
                  )}
                </div>
                <button className="expand-button">
                  {expandedStage === stageData.stage.id ? (
                    <FaChevronUp />
                  ) : (
                    <FaChevronDown />
                  )}
                </button>
              </div>

              {expandedStage === stageData.stage.id && (
                <div className="playbook-card-body">
                  <div className="playbook-form">
                    <div className="form-row">
                      <div className="form-field">
                        <label>Nome do Playbook</label>
                        <input
                          type="text"
                          value={formData.name}
                          onChange={(e) =>
                            setFormData({ ...formData, name: e.target.value })
                          }
                          placeholder={`Playbook - ${stageData.stage.name}`}
                        />
                      </div>
                    </div>

                    <div className="form-field">
                      <label>Objetivo desta Etapa</label>
                      <textarea
                        value={formData.objective}
                        onChange={(e) =>
                          setFormData({ ...formData, objective: e.target.value })
                        }
                        placeholder="Ex: Qualificar o lead e entender suas necessidades"
                        rows={2}
                      />
                    </div>

                    <div className="form-field">
                      <label>Mensagem de Abertura</label>
                      <textarea
                        value={formData.opening_message}
                        onChange={(e) =>
                          setFormData({ ...formData, opening_message: e.target.value })
                        }
                        placeholder="Mensagem sugerida para iniciar a conversa nesta etapa"
                        rows={2}
                      />
                    </div>

                    <div className="form-field">
                      <label>
                        <FaQuestionCircle /> Perguntas para Qualificacao
                      </label>
                      <div className="question-input">
                        <input
                          type="text"
                          value={newQuestion.question}
                          onChange={(e) =>
                            setNewQuestion({ ...newQuestion, question: e.target.value })
                          }
                          placeholder="Pergunta"
                        />
                        <input
                          type="text"
                          value={newQuestion.purpose}
                          onChange={(e) =>
                            setNewQuestion({ ...newQuestion, purpose: e.target.value })
                          }
                          placeholder="Proposito (opcional)"
                        />
                        <button type="button" className="buttonAdicionar" onClick={handleAddQuestion}>
                          <FaPlus />
                        </button>
                      </div>
                      <div className="questions-list">
                        {formData.questions_to_ask.map((q, index) => (
                          <div key={index} className="question-item">
                            <span className="question-order">{q.order}</span>
                            <div className="question-content">
                              <p>{q.question}</p>
                              {q.purpose && <small>Proposito: {q.purpose}</small>}
                            </div>
                            <button onClick={() => handleRemoveQuestion(index)}>
                              <FaTimes />
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>

                    <div className="form-field">
                      <label>
                        <FaExclamationTriangle /> Tratamento de Objecoes
                      </label>
                      <div className="objection-input">
                        <input
                          type="text"
                          value={newObjection.objection}
                          onChange={(e) =>
                            setNewObjection({ ...newObjection, objection: e.target.value })
                          }
                          placeholder='Se disser: "muito caro"'
                        />
                        <input
                          type="text"
                          value={newObjection.response}
                          onChange={(e) =>
                            setNewObjection({ ...newObjection, response: e.target.value })
                          }
                          placeholder="Responder com..."
                        />
                        <button type="button"  className="buttonAdicionar" onClick={handleAddObjection}>
                          <FaPlus />
                        </button>
                      </div>
                      <div className="objections-list">
                        {formData.objections_handling.map((obj, index) => (
                          <div key={index} className="objection-item">
                            <div className="objection-content">
                              <strong>Se disser: "{obj.objection}"</strong>
                              <p>Responder: {obj.response}</p>
                            </div>
                            <button onClick={() => handleRemoveObjection(index)}>
                              <FaTimes />
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>

                    <div className="form-row">
                      <div className="form-field">
                        <label>Criterios de Sucesso</label>
                        <textarea
                          value={formData.success_criteria}
                          onChange={(e) =>
                            setFormData({ ...formData, success_criteria: e.target.value })
                          }
                          placeholder="Quando considerar que esta etapa foi bem sucedida"
                          rows={2}
                        />
                      </div>

                      <div className="form-field">
                        <label>
                          <FaArrowRight /> Proxima Etapa
                        </label>
                        <select
                          value={formData.next_stage_id}
                          onChange={(e) =>
                            setFormData({ ...formData, next_stage_id: e.target.value })
                          }
                        >
                          <option value="">Nenhuma (manual)</option>
                          {getOtherStages(stageData.stage.id).map((stage) => (
                            <option key={stage.id} value={stage.id}>
                              {stage.name}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>

                    <div className="form-field">
                      <label>Gatilhos de Escalacao</label>
                      <p className="field-hint">
                        Palavras que transferem para atendente humano
                      </p>
                      <div className="tag-input-container">
                        <input
                          type="text"
                          value={newEscalationTrigger}
                          onChange={(e) => setNewEscalationTrigger(e.target.value)}
                          placeholder="Ex: cancelar, reclamacao"
                          onKeyPress={(e) =>
                            e.key === "Enter" && handleAddEscalationTrigger()
                          }
                        />
                        <button type="button"  className="buttonAdicionar" onClick={handleAddEscalationTrigger}>
                          Adicionar
                        </button>
                      </div>
                      <div className="tags-list">
                        {formData.escalation_triggers.map((trigger) => (
                          <span key={trigger} className="tag">
                            {trigger}
                            <button onClick={() => handleRemoveEscalationTrigger(trigger)}>
                              &times;
                            </button>
                          </span>
                        ))}
                      </div>
                    </div>

                    <div className="form-field">
                      <label>Instrucoes Personalizadas</label>
                      <textarea
                        value={formData.custom_instructions}
                        onChange={(e) =>
                          setFormData({ ...formData, custom_instructions: e.target.value })
                        }
                        placeholder="Instrucoes adicionais especificas para esta etapa..."
                        rows={3}
                      />
                    </div>

                    <div className="playbook-actions">
                      {stageData.playbook && (
                        <button
                          className="delete-button"
                          onClick={() =>
                            handleDelete(stageData.playbook.id, stageData.stage.id)
                          }
                        >
                          Excluir Playbook
                        </button>
                      )}
                      <button
                        className="save-button"
                        onClick={() => handleSave(stageData.stage.id)}
                        disabled={loading}
                      >
                        <FaSave /> {loading ? "Salvando..." : "Salvar Playbook"}
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default PlaybookEditor;
